import { createContext, ReactNode, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { DEBUG_SOCKET } from "src/config/constants";
import { useAppDispatch, useAppSelector } from "src/redux/redux-hooks";
import socketServices from "./socketService";
import fancySocketServices from "./fancySocketServices";

interface SocketRoomTypes {
  getSocketOddsIds: () => any[];
  getSocketFancyIds: () => any[];
  getSocketEventId: () => { eventId: string } | undefined;
  resetOddsIds: () => void;
  resetFancyIds: () => void;
  removeOddsId: (name: string) => void;
  handleEventRoomIds: (data: { eventId: string }) => void;
  updateCount: number;
  betSuccess: { betId: string }[];
  updateBetSuccess: (betId: string) => void;
}

const SocketRoomContext = createContext<SocketRoomTypes>({
  getSocketOddsIds: () => [],
  getSocketFancyIds: () => [],
  getSocketEventId: () => undefined,
  resetOddsIds: () => {},
  resetFancyIds: () => {},
  removeOddsId: () => {},
  handleEventRoomIds: () => {},
  updateCount: 1,
  betSuccess: [],
  updateBetSuccess: () => {},
});

const SocketRoomIdProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const [updateCount, setUpdateCount] = useState<number>(1);
  const { oddsSocketConnected, fancySocketConnected } = useAppSelector(({ socket }) => socket);
  const { isLoggedIn } = useAppSelector(({ user }) => user);

  const socketEventIdsRef = useRef<{ eventId: string }>();
  const socketOddsIdsRef = useRef<any[]>([]);
  const socketFancyIdsRef = useRef<any[]>([]);

  const handleEventRoomIds = (data: { eventId: string }) => {
    if (data && data.eventId) {
      if (DEBUG_SOCKET) console.log(`🚀 ~ EVENT JOINED:`, data);
      socketEventIdsRef.current = data;
      setUpdateCount((prev) => prev + 1);
    }
  };

  const handleOddsRoomIds = (data: { data: any; response_error: any }) => {
    if (data && data.data && !data.response_error) {
      if (DEBUG_SOCKET) console.log(`🚀 ~ ODDS JOINED:`, data);
      socketOddsIdsRef.current.push(data.data);
      setUpdateCount((prev) => prev + 1);
    }
  };

  const handleFancyRoomIds = (data: { data: any; response_error: any }) => {
    if (data && data.data && !data.response_error) {
      if (DEBUG_SOCKET) console.log(`🚀 ~ FANCY JOINED:`, data);
      socketFancyIdsRef.current.push(data.data);
      setUpdateCount((prev) => prev + 1);
    }
  };

  const leaveAllRoom = () => {
    const socketOddsIds: any[] = getSocketOddsIds();
    if (DEBUG_SOCKET) console.log(`🚀 ~ socketOddsIds !!:`, socketOddsIds);
    if (socketOddsIds.length > 0 && oddsSocketConnected) {
      socketOddsIds.forEach((data: any) => {
        if (DEBUG_SOCKET) console.log(`🚀 ~ LEAVE_ROOM EXP:`, data);
        socketServices.leaveRoom(data);
      });
    }

    const socketFancyIds: any[] = getSocketFancyIds();
    if (DEBUG_SOCKET) console.log(`🚀 ~ socketFancyIds !!:`, socketFancyIds);
    if (socketFancyIds.length > 0 && fancySocketConnected) {
      socketFancyIds.forEach((data: any) => {
        if (DEBUG_SOCKET) console.log(`🚀 ~ LEAVE_FANCY_ROOM EXP:`, data);
        fancySocketServices.leaveRoom(data);
      });
    }
    resetFancyIds();
    resetOddsIds();
  };

  const resetOddsIds = () => {
    socketOddsIdsRef.current = [];
    setUpdateCount((prev) => prev + 1);
  };

  const resetFancyIds = () => {
    socketFancyIdsRef.current = [];
    setUpdateCount((prev) => prev + 1);
  };

  const removeOddsId = (name: string) => {
    socketOddsIdsRef.current = socketOddsIdsRef.current.filter((item) => item.roomNames !== name);
    setUpdateCount((prev) => prev + 1);
  };

  const getSocketOddsIds = (): any[] => socketOddsIdsRef.current;
  const getSocketFancyIds = (): any[] => socketFancyIdsRef.current;
  const getSocketEventId = (): { eventId: string } | undefined => socketEventIdsRef.current;

  useEffect(() => {
    // store room id to leave
    if (oddsSocketConnected) socketServices.roomJoined(handleOddsRoomIds);
    socketServices.socketError();
  }, [oddsSocketConnected]);

  useEffect(() => {
    // store room id to leave
    if (fancySocketConnected) fancySocketServices.roomJoined(handleFancyRoomIds);
    fancySocketServices.socketError();
  }, [fancySocketConnected]);

  const [betSuccess, setbetSuccess] = useState<{ betId: string }[]>([]);

  // const { betDetail, betLoading } = useAppSelector(({ bet }) => bet);
  // const { user } = useAppSelector(({ auth }) => auth);

  // const idRef = useRef<Record<string, any>>({});

  // useEffect(() => {
  //   idRef.current = {
  //     is_fancy: betDetail?.is_fancy ? true : false,
  //     event_id: betDetail?.event_id || '',
  //     provider_market_id: betDetail?.provider_market_id || '',
  //     is_demo: user?.is_demo ? true : false,
  //   };
  // }, [betDetail?.is_fancy, betDetail?.event_id, betDetail?.provider_market_id, user?.is_demo]);

  // useEffect(() => {
  //   if (betSuccess.length > 0) {
  //     console.log('SHOW BET SUCCESS:', betSuccess);
  //     enqueueSnackbar('Bet Placed Successfully', { variant: 'success' });

  //     if (idRef.current.is_fancy) {
  //       dispatch(getFancyExposure(idRef.current.event_id));
  //     } else {
  //       dispatch(
  //         betAction.updateReFetchExposures({ [idRef.current.provider_market_id]: getRandomId() })
  //       );
  //     }
  //     if (!user?.is_demo) {
  //       dispatch(getOpenBets('matched'));
  //     }
  //     dispatch(getUserExposure());
  //     dispatch(fetchUserBalance());

  //     // Reset bet detail and loading state after a short delay to ensure all updates are processed
  //     setTimeout(() => {
  //       dispatch(betAction.resetBetDetail());
  //       dispatch(betAction.setBetLoading(false));
  //     }, 50);
  //   }
  // }, [JSON.stringify(betSuccess)]);

  const updateBetSuccess = (betId: string) => {
    setbetSuccess((prev) => {
      const exists = prev.some((item) => item.betId === betId);
      if (exists) {
        return [...prev];
      }

      return [...prev, { betId }];
    });
  };

  // useEffect(() => {
  //   if (!['/race-details/', '/game-details/'].some((path) => pathname.includes(path))) {
  //     setTimeout(() => {
  //       leaveAllRoom();
  //     }, 11);
  //   }
  // }, [pathname]);

  return (
    <SocketRoomContext.Provider
      value={{
        handleEventRoomIds,
        getSocketEventId,
        getSocketOddsIds,
        getSocketFancyIds,
        resetOddsIds,
        resetFancyIds,
        removeOddsId,
        updateCount,
        betSuccess,
        updateBetSuccess,
      }}
    >
      {children}
    </SocketRoomContext.Provider>
  );
};

export const useSocketRoomIds = () => {
  const context = useContext(SocketRoomContext);
  if (!context) {
    throw new Error("useSocketRoomIds must be used within a SocketRoomIdProvider");
  }
  return context;
};

export default SocketRoomIdProvider;
